'use client';

import * as React from 'react';
import { Check, ListChecks, ScanSearch, Send } from 'lucide-react';
import { motion, useReducedMotion } from 'motion/react';

import { MCP_WHAT_IS } from '~/lib/mcp-content';

type Pillar = (typeof MCP_WHAT_IS.pillars)[number];

const PILLAR_ICONS: Record<
  string,
  React.ComponentType<{ className?: string }>
> = {
  'scan-search': ScanSearch,
  'list-checks': ListChecks,
  send: Send
};

/**
 * §3 What Lumnis MCP is. Three pillars (Discover / Decide / Act), each with a
 * short framing line and the concrete tool capabilities folded in as bullets.
 */
export function McpWhatIs(): React.JSX.Element {
  const shouldReduceMotion = useReducedMotion();
  const reduced = !!shouldReduceMotion;

  return (
    <section className="relative bg-[color:var(--paper)] py-20 md:py-28">
      <div className="w-full mx-auto max-w-[1320px] px-6 md:px-8 lg:px-12 xl:px-16">
        <motion.div
          className="mx-auto max-w-[760px] text-center mb-12 md:mb-16"
          initial={{ opacity: 0, y: reduced ? 0 : 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.5 }}
        >
          <span className="eyebrow block mb-3">{MCP_WHAT_IS.eyebrow}</span>
          <h2 className="display-h2 text-3xl md:text-4xl lg:text-5xl text-[color:var(--ink)] [text-wrap:balance]">
            <span className="text-[color:var(--ink-3)]">
              {MCP_WHAT_IS.headlineLead}
            </span>{' '}
            <span>{MCP_WHAT_IS.headlineRest}</span>
          </h2>
          <p className="mt-5 text-base md:text-lg text-[color:var(--ink-3)] leading-relaxed [text-wrap:balance]">
            {MCP_WHAT_IS.description}
          </p>
        </motion.div>

        <div className="divide-y divide-[color:var(--hairline)] border-y border-[color:var(--hairline)]">
          {MCP_WHAT_IS.pillars.map((pillar, i) => (
            <PillarCard
              key={pillar.id}
              pillar={pillar}
              index={i}
              reduced={reduced}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function PillarCard({
  pillar,
  index,
  reduced
}: {
  pillar: Pillar;
  index: number;
  reduced: boolean;
}): React.JSX.Element {
  const Icon = PILLAR_ICONS[pillar.icon] ?? ScanSearch;

  return (
    <motion.article
      className="grid gap-x-8 py-8 md:grid-cols-[3rem_1fr_1.15fr] md:items-start"
      initial={{ opacity: 0, y: reduced ? 0 : 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-70px' }}
      transition={{
        delay: reduced ? 0 : index * 0.08,
        duration: 0.45,
        ease: 'easeOut'
      }}
    >
      <span
        className="flex h-11 w-11 items-center justify-center rounded-xl border border-teal-500/20"
        style={{ background: 'var(--mint)', color: 'var(--accent-darker)' }}
      >
        <Icon className="h-5 w-5" />
      </span>

      <div>
        <h3 className="mt-4 text-xl font-semibold text-[color:var(--ink)] md:mt-0">
          {pillar.title}
        </h3>
        <p className="mt-2 max-w-sm text-[15px] leading-relaxed text-[color:var(--ink-3)]">
          {pillar.body}
        </p>
      </div>

      <ul className="mt-5 flex flex-col gap-2.5 md:mt-0">
        {pillar.capabilities.map((cap) => (
          <li
            key={cap}
            className="flex items-start gap-2.5 text-[13.5px] text-[color:var(--ink-2)]"
          >
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--accent-deep)]" />
            <span>{cap}</span>
          </li>
        ))}
      </ul>
    </motion.article>
  );
}
